/**
 * unspun blog end-of-article signup — client-side logic
 *
 * Mirrors pdp-signup.js with three differences:
 *  1. Different DOM ids (`blog-signup-*` instead of `pdp-signup-*`)
 *  2. signup_source: blog_<article-handle> for downstream attribution
 *  3. Shares the localStorage submitted-flag with PDP signup so a visitor who already submitted
 *     on a PDP doesn't get prompted again on a blog article (and vice versa).
 *
 * Last updated: 2026-05-05
 */

(function () {
  'use strict';

  var KLAVIYO_COMPANY_ID = 'U3dkRr';
  var KLAVIYO_MASTER_LIST_ID = 'U22MgP';
  var KLAVIYO_SUBSCRIBE_URL =
    'https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID;
  var KLAVIYO_PROFILES_URL =
    'https://a.klaviyo.com/client/profiles/?company_id=' + KLAVIYO_COMPANY_ID;
  var KLAVIYO_REVISION = '2024-10-15';

  // Shared with pdp-signup.js — once submitted anywhere, suppressed everywhere.
  var LS_KEY = 'unspun-pdp-signup-state';

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  function readKlaviyoCookie() {
    var raw = getCookie('__kla_id');
    if (!raw) return null;
    try {
      return JSON.parse(atob(raw));
    } catch (e) {
      return null;
    }
  }

  function isKlaviyoIdentified() {
    var cookie = readKlaviyoCookie();
    return cookie ? Boolean(cookie['$exchange_id']) : false;
  }

  function hasSubmitted() {
    try {
      var raw = localStorage.getItem(LS_KEY);
      if (!raw) return false;
      var state = JSON.parse(raw);
      return Boolean(state && state.submitted);
    } catch (e) {
      return false;
    }
  }

  function markSubmitted() {
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({ submitted: true, at: new Date().toISOString() })
      );
    } catch (e) {
      // Quota / private mode — non-fatal.
    }
  }

  function isValidEmail(email) {
    if (!email || typeof email !== 'string') return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  function setupRevealAnimation(section) {
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    section.classList.add('blog-signup-pre-reveal');
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            section.classList.remove('blog-signup-pre-reveal');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    observer.observe(section);
  }

  function submitToKlaviyo(email, articleHandle) {
    var signupSource = articleHandle ? 'blog_' + articleHandle : 'blog_unknown';

    var profilePayload = {
      data: {
        type: 'profile',
        attributes: {
          email: email,
          properties: {
            signup_source: signupSource
          }
        }
      }
    };

    return fetch(KLAVIYO_PROFILES_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(profilePayload)
    })
      .then(function (resp) {
        if (resp.status !== 202 && resp.status !== 200 && resp.status !== 409) {
          return resp.text().then(function (body) {
            throw new Error(
              'Profile create failed (' + resp.status + '): ' + body
            );
          });
        }
        var subscribePayload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Blog signup form (' + signupSource + ')',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: KLAVIYO_MASTER_LIST_ID
                }
              }
            }
          }
        };

        return fetch(KLAVIYO_SUBSCRIBE_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(subscribePayload)
        });
      })
      .then(function (resp) {
        if (resp.status !== 202 && resp.status !== 200) {
          return resp.text().then(function (body) {
            throw new Error('Subscribe failed (' + resp.status + '): ' + body);
          });
        }
        return true;
      });
  }

  function showConfirmationState() {
    if (window._tfa) { try { window._tfa.push({notify: 'event', name: 'lead', id: 2050036}); } catch (e) {} }
    var formState = document.getElementById('blog-signup-form-state');
    var confState = document.getElementById('blog-signup-confirmation-state');
    if (formState) formState.style.display = 'none';
    if (confState) confState.style.display = '';
  }

  function showError(message) {
    var errEl = document.getElementById('blog-signup-error');
    if (!errEl) return;
    errEl.textContent = message;
    errEl.style.display = '';
  }

  function clearError() {
    var errEl = document.getElementById('blog-signup-error');
    if (!errEl) return;
    errEl.textContent = '';
    errEl.style.display = 'none';
  }

  function setSubmittingUI(submitting) {
    var btn = document.getElementById('blog-signup-submit');
    var textSpan = btn ? btn.querySelector('.blog-signup-submit-text') : null;
    var loadingSpan = btn
      ? btn.querySelector('.blog-signup-submit-loading')
      : null;
    if (btn) btn.disabled = submitting;
    if (textSpan) textSpan.style.display = submitting ? 'none' : '';
    if (loadingSpan) loadingSpan.style.display = submitting ? '' : 'none';
  }

  function init() {
    var section = document.getElementById('blog-signup-section');
    if (!section) return;

    if (isKlaviyoIdentified() || hasSubmitted()) {
      // Stay hidden.
      return;
    }

    section.style.display = '';
    setupRevealAnimation(section);

    var form = document.getElementById('blog-signup-form');
    var emailInput = document.getElementById('blog-signup-email');
    var articleHandle = section.getAttribute('data-article-handle') || '';

    if (!form || !emailInput) return;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      clearError();

      var email = emailInput.value.trim();
      if (!isValidEmail(email)) {
        showError('Please enter a valid email address.');
        return;
      }

      setSubmittingUI(true);

      submitToKlaviyo(email, articleHandle)
        .then(function () {
          markSubmitted();
          setSubmittingUI(false);
          showConfirmationState();
        })
        .catch(function (err) {
          if (window.console && typeof console.warn === 'function') {
            console.warn('[blog-signup] submission failed:', err);
          }
          setSubmittingUI(false);
          showError(
            'Something went wrong. Please try again, or email joe@beunspun.co.uk.'
          );
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
